import { useState } from 'react';
import { tokenManager } from '../config/authConfig';
import API_CIFRA from '../config/Variables_apis';

const useArchivos = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Función para obtener headers de autenticación (sin Content-Type para FormData)
  const getAuthHeaders = () => {
    const token = tokenManager.getAccessToken();
    const headers = {};
    
    if (token && !tokenManager.isTokenExpired()) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    return headers;
  };

  // Función para manejar errores específicos
  const handleApiError = (error, response) => {
    let errorMessage = "Error al subir el archivo";
    
    if (response?.status === 400) {
      errorMessage = "Archivo inválido. Verifica el formato y tamaño.";
    } else if (response?.status === 401) {
      errorMessage = "Error de autenticación. Intenta nuevamente.";
    } else if (response?.status === 403) {
      errorMessage = "No tienes permisos para subir archivos.";
    } else if (response?.status === 404) {
      errorMessage = "Recurso no encontrado.";
    } else if (response?.status === 413) {
      errorMessage = "Archivo demasiado grande. Reduce el tamaño.";
    } else if (response?.status === 500) {
      errorMessage = "Error interno del servidor. Intenta más tarde.";
    }
    
    return errorMessage;
  };

  // Función para subir archivo
  const subirArchivo = async (archivo, incidenciaId, latitud = null, longitud = null) => {
    setLoading(true);
    setError(null);

    try {
      console.log('📤 === INICIANDO SUBIDA DE ARCHIVO ===');
      console.log('📤 Archivo recibido:', archivo);
      console.log('📤 Tipo de archivo:', typeof archivo);
      console.log('📤 Es File?', archivo instanceof File);
      console.log('📤 Es Blob?', archivo instanceof Blob);
      
      if (!archivo) {
        throw new Error('Archivo es null o undefined');
      }
      
      if (!(archivo instanceof File) && !(archivo instanceof Blob)) {
        throw new Error(`Archivo no es válido. Tipo: ${typeof archivo}`);
      }
      
      console.log('📤 Nombre del archivo:', archivo.name);
      console.log('📤 Tipo del archivo:', archivo.type);
      console.log('📤 Tamaño del archivo:', archivo.size);
      console.log('📤 ID de incidencia:', incidenciaId);
      console.log('📤 Coordenadas:', { latitud, longitud });
      
      // Crear FormData para el archivo
      const formData = new FormData();
      formData.append('arc_fileArchivo', archivo); // ✅ Campo correcto que espera el backend
      formData.append('fk_incidencia', incidenciaId);
      
      // Agregar nombre del archivo si no tiene
      if (!archivo.name) {
        const inferredExt = (archivo.type && archivo.type.includes('video')) ? '.webm'
                          : (archivo.type && archivo.type.includes('audio')) ? '.webm'
                          : (archivo.type && archivo.type.includes('image')) ? '.jpg'
                          : '';
        formData.append('arc_chNombre', `archivo_${Date.now()}${inferredExt}`);
      } else {
        formData.append('arc_chNombre', archivo.name);
      }

      // Agregar tipo de archivo basado en el MIME type
      if (archivo.type) {
        if (archivo.type.startsWith('image/')) {
          formData.append('arc_chTipoArchivo', 'Imagen');
        } else if (archivo.type.startsWith('video/')) {
          formData.append('arc_chTipoArchivo', 'Video');
        } else if (archivo.type.startsWith('audio/')) {
          formData.append('arc_chTipoArchivo', 'Audio');
        } else {
          formData.append('arc_chTipoArchivo', 'Documento');
        }
      }

      // Agregar coordenadas si están disponibles
      if (latitud !== null && longitud !== null) {
        formData.append('arc_deLatitud', latitud.toString());
        formData.append('arc_deLongitud', longitud.toString());
        console.log('📤 Coordenadas agregadas al FormData:', { latitud, longitud });
      }

      // Determinar el tipo de archivo que se está enviando
      let tipoArchivo = 'Documento';
      if (archivo.type) {
        if (archivo.type.startsWith('image/')) {
          tipoArchivo = 'Imagen';
        } else if (archivo.type.startsWith('video/')) {
          tipoArchivo = 'Video';
        } else if (archivo.type.startsWith('audio/')) {
          tipoArchivo = 'Audio';
        }
      }
      
      console.log('📤 FormData creado:', {
        arc_fileArchivo: archivo.name,
        fk_incidencia: incidenciaId,
        arc_chNombre: archivo.name || `archivo_${Date.now()}`,
        arc_chTipoArchivo: tipoArchivo,
        arc_deLatitud: latitud,
        arc_deLongitud: longitud
      });

      const response = await fetch(API_CIFRA.archivo.upload, {
        method: 'POST',
        headers: getAuthHeaders(), // Sin Content-Type para FormData
        body: formData,
      });

      const responseData = await response.json();
      console.log('📤 Respuesta del servidor:', responseData);

      if (!response.ok) {
        console.error('❌ Error en la respuesta del servidor:', response.status, responseData);
        const errorMessage = handleApiError(error, response);
        throw new Error(responseData.error || responseData.detail || errorMessage);
      }

      console.log('✅ Archivo subido exitosamente:', responseData.arc_inId);
      return { 
        success: true, 
        data: responseData,
        archivoId: responseData.arc_inId
      };

    } catch (error) {
      const errorMessage = error.message || "Error al subir el archivo";
      console.error('❌ === ERROR SUBIENDO ARCHIVO ===');
      console.error('❌ Mensaje de error:', errorMessage);
      console.error('❌ Error completo:', error);
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener archivos de una incidencia
  const obtenerArchivosIncidencia = async (incidenciaId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_CIFRA.archivo.listarPorIncidencia(incidenciaId), {
        method: 'GET',
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = handleApiError(error, response);
        throw new Error(data.error || data.detail || errorMessage);
      }

      return { success: true, data };

    } catch (error) {
      const errorMessage = error.message || "Error al obtener archivos de la incidencia";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Función para descargar archivo
  const descargarArchivo = async (archivoId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_CIFRA.archivo.download(archivoId), {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorMessage = handleApiError(error, response);
        throw new Error(errorMessage);
      }

      // Crear blob y descargar
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `archivo_${archivoId}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { success: true };

    } catch (error) {
      const errorMessage = error.message || "Error al descargar el archivo";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener información del archivo
  const obtenerInfoArchivo = async (archivoId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_CIFRA.archivo.info(archivoId), {
        method: 'GET',
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = handleApiError(error, response);
        throw new Error(data.error || data.detail || errorMessage);
      }

      return { success: true, data };

    } catch (error) {
      const errorMessage = error.message || "Error al obtener información del archivo";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Función para eliminar archivo
  const eliminarArchivo = async (archivoId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_CIFRA.archivo.eliminar(archivoId), {
        method: 'DELETE',
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = handleApiError(error, response);
        throw new Error(data.error || data.detail || errorMessage);
      }

      return { success: true, data };

    } catch (error) {
      const errorMessage = error.message || "Error al eliminar el archivo";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  return {
    // Estado
    loading,
    error,
    
    // Funciones
    subirArchivo,
    obtenerArchivosIncidencia,
    descargarArchivo,
    obtenerInfoArchivo,
    eliminarArchivo,
  };
};

export default useArchivos; 